import { useState } from 'react';

import { Notice } from '../../../common/components/detail/detail';
import { useRepositoryName } from '../../../global/contexts/lookups/lookups';
import { usePlanVersion } from '../contexts/plans';
import { countChanges, diffLines, diffScope } from '../utils/diff';
import { comparable } from '../utils/version';
import { VERSION_STATE_LABEL } from '../utils/form';

import '../styles/plans.css';

// Comparing two versions of a plan.
//
// The honesty rule of this screen: IF ONE SIDE CANNOT BE READ, SAY SO. A
// version that is out of scope or whose request failed is not an empty
// document. Diffing against "" would report the entire plan as deleted — a
// wrong answer delivered with total confidence, which is worse than no answer.

export default function CompareVersions({ planId, versions, current }) {
    const numbers = versions.map(v => v.number);
    const [left, setLeft] = useState(() => (
        numbers.find(n => String(n) !== String(current)) ?? numbers[0]
    ));
    const right = current;

    const a = usePlanVersion(planId, left);
    const b = usePlanVersion(planId, right);

    const loading = a.isLoading || b.isLoading;
    const failed = a.error || b.error;

    return (
        <section className="plans__compare">
            <div className="plans__compare-head">
                <label className="plans__compare-pick">
                    <span>Comparar la versión</span>
                    <select
                        className="plans__compare-select"
                        value={String(left)}
                        onChange={e => setLeft(e.target.value)}
                    >
                        {versions.map(version => (
                            <option key={version.number} value={String(version.number)}>
                                v{version.number} · {VERSION_STATE_LABEL[version.state] ?? version.state}
                            </option>
                        ))}
                    </select>
                </label>
                <span className="plans__compare-with">con la versión {right}</span>
            </div>

            {loading && <p className="plans__note">Cargando las dos versiones...</p>}

            {failed && (
                <Notice tone="warn">
                    No se pudo leer {a.error ? `la versión ${left}` : `la versión ${right}`}
                    {' '}—{' '}
                    {(a.error ?? b.error)?.status === 404
                        ? 'no existe o queda fuera de tu alcance'
                        : (a.error ?? b.error)?.message}
                    . <strong>No se muestra una comparación incompleta</strong>: tratar la versión
                    que falta como un documento vacío diría que se borró todo, que no es lo que pasó.
                </Notice>
            )}

            {!loading && !failed && (
                <CompareBody left={left} right={right} before={a.data} after={b.data} />
            )}
        </section>
    );
}

// `before` and `after` are adapted models. Same honesty rule as a failed
// request: a side whose text or scope could not be read is SAID, not diffed
// as empty or as the whole project.
function CompareBody({ left, right, before, after }) {
    const check = comparable(before, after);
    const rows = check.content ? diffLines(before.content, after.content) : [];
    const { added, removed } = countChanges(rows);
    const scope = check.scope ? diffScope(before.scope.repository_ids, after.scope.repository_ids) : null;

    if (!check.content) {
        return (
            <Notice tone="warn">
                La v{check.missingContent.includes('before') ? left : right} no tiene contenido que
                leer. <strong>No se compara contra un texto vacío</strong>: diría que se borró todo.
            </Notice>
        );
    }

    return (
        <>
            <p className="plans__note">
                {added === 0 && removed === 0
                    ? 'El texto es idéntico.'
                    : `${added} línea${added === 1 ? '' : 's'} agregada${added === 1 ? '' : 's'}, ${removed} quitada${removed === 1 ? '' : 's'}.`}
            </p>

            {scope
                ? <ScopeChange scope={scope} left={left} right={right} />
                : (
                    <Notice tone="warn">
                        No se pudo leer el alcance de una de las dos versiones, así que no se
                        dice si cambió.
                    </Notice>
                )}

            <div className="plans__diff">
                {rows.map((row, index) => (
                    <div key={index} className={`plans__diff-row plans__diff-row--${row.type}`}>
                        {/* The sign is a character and not only a colour: the
                            two themes and a reader without colour all have to
                            be able to tell added from removed. */}
                        <span className="plans__diff-sign" aria-hidden="true">
                            {row.type === 'added' ? '+' : row.type === 'removed' ? '−' : ' '}
                        </span>
                        <span className="plans__diff-text">{row.text || ' '}</span>
                    </div>
                ))}
            </div>
        </>
    );
}

// The repositories by name: «2 que entran» does not say which two.
function names(ids, repoName) {
    return ids.map(repoName).join(' · ');
}

function ScopeChange({ scope, left, right }) {
    const repoName = useRepositoryName();

    if (scope.kind === 'same-all') {
        return <p className="plans__note">Las dos rigen sobre <strong>todo el proyecto</strong>.</p>;
    }
    if (scope.kind === 'narrowed') {
        return (
            <p className="plans__note">
                El alcance <strong>se acotó</strong>: la v{left} regía sobre todo el proyecto y la
                v{right} rige sobre {scope.to.length === 1 ? 'el repositorio' : 'los repositorios'}{' '}
                <strong>{names(scope.to, repoName)}</strong>.
            </p>
        );
    }
    if (scope.kind === 'widened') {
        return (
            <p className="plans__note">
                El alcance <strong>se amplió</strong>: la v{left} regía sobre{' '}
                <strong>{names(scope.from, repoName)}</strong> y la v{right}
                {' '}rige sobre todo el proyecto.
            </p>
        );
    }
    return (
        <p className="plans__note">
            Alcance:{' '}
            {scope.added.length > 0
                ? <>entra{scope.added.length === 1 ? '' : 'n'} <strong>{names(scope.added, repoName)}</strong></>
                : 'no entra ninguno'}
            {'; '}
            {scope.removed.length > 0
                ? <>sale{scope.removed.length === 1 ? '' : 'n'} <strong>{names(scope.removed, repoName)}</strong></>
                : 'no sale ninguno'}
            .
        </p>
    );
}
